import { Card, RarityGem, type CardSize, type Rarity } from './Card.js';

/**
 * Wire-shape received from the AFL viewer state. Permissive because
 * other games (cricket, future TCGs) re-use the same `hand` slot with
 * different card types — fields are read defensively.
 */
export type HandCardData = {
  id?: string;
  kind?: string;
  name?: string;
  /** Short subtitle for player cards (e.g. "Tall forward", "Pace bowler"). */
  archetype?: string;
  hidden?: boolean;

  // Player-card fields
  position?: string;     // AFL: forward / mid / ruck / back
  role?: string;         // Cricket: batsman / bowler / all_rounder
  stats?: Record<string, number | undefined>;

  // Action-card fields (AFL)
  actionKind?: string;
  /** AFL action card: the plain-language effect shown on the face. */
  effect?: string;
  bonus?: number;
  /** Which player stat a modifier card (action/weather/stadium) buffs. */
  modifiesStat?: string;
  /** Where the modifier is played: shot / kick / mark_defense / smother / handpass / run / tackle. */
  window?: string;
  /**
   * SET / SUPPRESS / RELIEF action spec (folded in from the former 'call'
   * subclass). Present → this action OVERRULES (sets a base stat / disregards a
   * position rule / waives the stamina drain) for one contest or action, rather
   * than nudging a stat by +x/−x.
   */
  spec?: { type?: string; stat?: string; value?: number; mode?: string; target?: string };

  // Action-card fields (Cricket — discriminated by `subkind`)
  subkind?: string;      // 'shot' | 'delivery'
  shot?: string;
  delivery?: string;
  batBonus?: number;
  bowlBonus?: number;
  risk?: number;
  wicketWeight?: number;

  // Weather
  kickModifier?: number;     // AFL
  batterModifier?: number;   // Cricket

  // Stadium
  rating?: number;
  /** Time-of-day card: 0 day / 1 twilight / 2 night (skill −index on shots). */
  index?: number;
  indoor?: boolean;          // AFL only

  /** Recycle cost to play a modifier card (actions/weather/stadium). */
  cost?: number;

  /** Italic flavour line; surfaced by the preview modal. */
  flavor?: string;
  /** Optional card-art URL. When present it fills the recessed art well
   * (object-cover) in place of the crest + glyph — the slot for real art. */
  image?: string;
  /** Real rarity once the backend exposes one; mocked by kind otherwise. */
  rarity?: Rarity;

  /** Fine-grained position descriptor from the engine ("Full Back", "Wing"). */
  positionLabel?: string;
  /** Position tier (T1 rarest … T9 most flexible) from the position model. */
  positionTier?: string;
  /**
   * Set while a marking contest is waiting on this seat's card: the
   * position bonus this card would bring at the contest cell (0 = out of
   * position there). Drives the in-position badge.
   */
  contestBonus?: number;
  /** Human name of the contest cell the bonus was evaluated at. */
  contestCellName?: string;
  /**
   * Set while a tackle window is open for this seat: what committing THIS
   * card does against the runner (the owner's ladder is deterministic).
   */
  tackleOutcome?: 'ball_won' | 'runner_through' | 'stoppage';
  /** "T2 vs runner T1, speed 4 vs 3" — tooltip detail for the badge. */
  tackleDetail?: string;
};

/**
 * Fallback rarity for cards that don't carry one (older content,
 * cricket cards). Real cards now ship with `rarity` from the backend.
 */
export function fallbackRarity(kind: string | undefined): Rarity {
  switch (kind) {
    case 'stadium':
      return 'epic';
    case 'player':
      return 'rare';
    case 'action':
    case 'weather':
      return 'uncommon';
    default:
      return 'common';
  }
}

// ---------------------------------------------------------------------------
// Position colour language — the "guernsey colour" carried on the title bar
// and the frame's top edge so a hand reads at a glance. The fine descriptor +
// tier (shown in the art well) carry the real position meaning.
// ---------------------------------------------------------------------------
type Accent = { color: string; onText: string; tintRgb: string; bucket: string };
const POS_ACCENT: Record<string, Accent> = {
  forward: { color: 'var(--ec-pos-forward)', onText: 'var(--ec-on-forward)', tintRgb: 'var(--ec-pos-forward-rgb)', bucket: 'Forward' },
  midfielder: { color: 'var(--ec-pos-mid)', onText: 'var(--ec-on-mid)', tintRgb: 'var(--ec-pos-mid-rgb)', bucket: 'Midfield' },
  mid: { color: 'var(--ec-pos-mid)', onText: 'var(--ec-on-mid)', tintRgb: 'var(--ec-pos-mid-rgb)', bucket: 'Midfield' },
  ruck: { color: 'var(--ec-pos-ruck)', onText: 'var(--ec-on-ruck)', tintRgb: 'var(--ec-pos-ruck-rgb)', bucket: 'Ruck' },
  back: { color: 'var(--ec-pos-defence)', onText: 'var(--ec-on-defence)', tintRgb: 'var(--ec-pos-defence-rgb)', bucket: 'Defence' },
};
const EVENT_ACCENT: Accent = { color: 'var(--ec-pos-event)', onText: 'var(--ec-on-event)', tintRgb: 'var(--ec-pos-event-rgb)', bucket: 'Event' };

/** Map a card to its identity colour (title bar + frame edge). */
export function cardAccent(card: HandCardData): Accent {
  if (card.kind === 'player') {
    if (card.position && POS_ACCENT[card.position]) return POS_ACCENT[card.position]!;
    // Cricket roles borrow the nearest AFL guernsey.
    if (card.role === 'batsman') return POS_ACCENT.mid!;
    if (card.role === 'bowler') return POS_ACCENT.forward!;
    if (card.role === 'all_rounder') return POS_ACCENT.ruck!;
    return EVENT_ACCENT;
  }
  // Action / weather / stadium are the Event/Umpire bone class.
  return EVENT_ACCENT;
}

export type HandCardProps = {
  card: HandCardData;
  selected?: boolean;
  /** Adds a lit-foil edge indicating the card has at least one legal play now. */
  playable?: boolean;
  /** Card footprint. Defaults to 'md'; the viewport-fit hand strip uses 'sm'. */
  size?: CardSize;
  onClick?: () => void;
};

export function HandCard({ card, selected, playable, size = 'md', onClick }: HandCardProps) {
  if (card.hidden || !card.kind) {
    return <Card size={size} faceDown rarity="common" />;
  }

  const rarity = card.rarity ?? fallbackRarity(card.kind);
  const accent = cardAccent(card);

  return (
    <button
      type="button"
      onClick={onClick}
      className="block focus:outline-none"
      aria-label={card.name ?? 'card'}
    >
      <Card
        size={size}
        rarity={rarity}
        accentColor={accent.color}
        selected={selected}
        playable={playable}
        className="cursor-pointer"
      >
        <Header
          name={card.name ?? 'Unknown'}
          subtitle={card.archetype}
          cost={card.cost}
        />
        <ArtTile kind={card.kind} card={card} accent={accent} />
        <Body card={card} />
        <Footer rarity={rarity} />
      </Card>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Strips — bone card face. The title bar carries the position colour, the
// art well is a recessed dark window, the rules panel is bone, the footer
// band a shaded-bone strip with the set number.
// ---------------------------------------------------------------------------

function Header({
  name,
  subtitle,
  cost,
}: {
  name: string;
  subtitle?: string;
  cost?: number;
}) {
  // The header IS the rarity colour now — the brushed-metal foil (read from
  // `--foil`, set by the card's rarity class) under a slight dark wash so the
  // cream title stays legible on the lighter foils. No rarity text needed.
  return (
    <div
      style={{
        // Heavy dark wash over the rarity foil so cream text is readable on
        // EVERY tier — incl. the light golds/pewters (was light-on-light). The
        // foil still tints the bar (rarity reads by hue), and the bright foil
        // FRAME keeps the metallic shine.
        background: 'linear-gradient(180deg, rgba(18,15,11,0.62), rgba(18,15,11,0.82)), var(--foil)',
        color: '#f7f1e6',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -2px 0 -1px rgba(19,22,27,0.6)',
      }}
      className="relative flex items-center gap-1.5 px-1.5 py-1"
    >
      {/* Cost pip — recycle cost to play a modifier. */}
      {typeof cost === 'number' && cost > 0 && (
        <span
          title={`Cost: recycle ${cost} card${cost === 1 ? '' : 's'} from hand to deck to play this`}
          className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-ec-brass/70 bg-ec-ground px-1 font-display text-[9.4cqw] font-bold leading-none text-ec-brass"
        >
          {cost}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate font-display text-[10.4cqw] font-semibold uppercase leading-tight tracking-wide [text-shadow:0_1px_2px_rgba(0,0,0,0.85)]">
          {name}
        </div>
        {subtitle && (
          <div className="truncate font-display text-[8.3cqw] font-medium uppercase leading-tight tracking-wide opacity-85 [text-shadow:0_1px_2px_rgba(0,0,0,0.7)]">
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

function ArtTile({ kind, card, accent }: { kind: string; card: HandCardData; accent: Accent }) {
  // AFL → fine-grained descriptor when the engine sends one, else the
  // coarse position; cricket → role.
  const chip =
    kind === 'player' ? (card.positionLabel ?? card.position ?? card.role) : null;
  const bonus = card.contestBonus;
  return (
    <div
      className="relative flex flex-1 items-center justify-center overflow-hidden shadow-ec-art"
      style={{ background: 'linear-gradient(to bottom,var(--ec-art-2),var(--ec-art))' }}
    >
      {card.image ? (
        /* Real card art fills the well — the slot. */
        <img src={card.image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      ) : (
        <>
          {/* Spotlight tinted by the guernsey colour — lifts the figure. */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: `radial-gradient(120% 82% at 50% 22%, rgb(${accent.tintRgb} / 0.46), transparent 60%)` }}
            aria-hidden
          />
          {/* Fine engraving lines — old-card texture. */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:repeating-linear-gradient(90deg,#fff_0,#fff_1px,transparent_1px,transparent_5px)]"
            aria-hidden
          />
          {/* Heraldic crow crest, watermarked behind the figure. */}
          <CrestWatermark />
          {/* The figure, lifted with a drop shadow (DOM order keeps the badges
              below — which render after the vignette — on top). */}
          <div className="relative flex items-center justify-center drop-shadow-[0_3px_4px_rgba(0,0,0,0.6)]">
            <KindGlyph kind={kind} card={card} />
          </div>
        </>
      )}
      {/* Vignette — frames the art well. */}
      <div
        className="pointer-events-none absolute inset-0 [background:radial-gradient(ellipse_at_center,transparent_42%,rgba(0,0,0,0.6))]"
        aria-hidden
      />
      {/* OVERRIDE banner — Calls and Conditions OVERRULE, they do not nudge. A
          loud ribbon so a hand reads "this overrides" at a glance, distinct from
          the +x/−x modifier actions. */}
      {(card.spec || kind === 'condition') && <OverrideRibbon kind={kind} card={card} />}
      {chip && (
        <span className="absolute right-1 top-1 max-w-[78%] truncate rounded-sm border border-ec-line bg-ec-ground/85 px-1 py-0.5 font-display text-[8.3cqw] uppercase tracking-wider text-ec-bone">
          {chip.replace(/_/g, ' ')}
        </span>
      )}
      {/* Position tier (T1 rarest → T9 most flexible) — the spine of the
          position model, so it reads on the card face, not just in a match. */}
      {kind === 'player' && card.positionTier && (
        <span
          title={`Position tier ${String(card.positionTier).replace(/^T/i, '')} — tier 1 is the rarest position (biggest in-position bonus), tier 9 the most flexible`}
          className="absolute left-1 top-1 rounded-sm border border-ec-brass/60 bg-ec-ground/85 px-1 py-0.5 font-display text-[8.3cqw] font-bold leading-none text-ec-brass"
        >
          {card.positionTier}
        </span>
      )}
      {/* In-position badge — shown while a contest is asking for a card. */}
      {typeof bonus === 'number' && (
        <span
          title={
            bonus > 0
              ? `In position at ${card.contestCellName ?? 'the contest'} — adds +${bonus}`
              : `Out of position at ${card.contestCellName ?? 'the contest'} — no bonus`
          }
          className={`absolute bottom-1 left-1 rounded-full border px-1 py-0.5 font-display text-[8.3cqw] font-bold leading-none ${
            bonus > 0
              ? 'border-ec-defence bg-ec-defence text-ec-ink shadow-[0_0_8px_rgba(85,167,47,0.8)]'
              : 'border-ec-line bg-ec-ground/80 text-ec-bone-dim'
          }`}
        >
          {bonus > 0 ? `+${bonus}` : '·0'}
        </span>
      )}
      {/* Tackle badge — what this card does against the current runner. */}
      {card.tackleOutcome && (
        <span
          title={`${
            card.tackleOutcome === 'ball_won'
              ? 'Wins the ball'
              : card.tackleOutcome === 'stoppage'
                ? 'Dead tie — forces a ball-up'
                : "Can't stop the runner"
          }${card.tackleDetail ? ` (${card.tackleDetail})` : ''}`}
          className={`absolute bottom-1 left-1 rounded-full border px-1 py-0.5 font-display text-[8.3cqw] font-bold leading-none ${
            card.tackleOutcome === 'ball_won'
              ? 'border-ec-defence bg-ec-defence text-ec-ink shadow-[0_0_8px_rgba(85,167,47,0.8)]'
              : card.tackleOutcome === 'stoppage'
                ? 'border-ec-brass bg-ec-brass text-ec-ink'
                : 'border-ec-line bg-ec-ground/80 text-ec-bone-dim'
          }`}
        >
          {card.tackleOutcome === 'ball_won' ? '✓ stops' : card.tackleOutcome === 'stoppage' ? 'ruck' : "✗ can't"}
        </span>
      )}
    </div>
  );
}

/**
 * The OVERRIDE banner for a set/suppress/relief Action and for a Conditions card
 * — the structural tell that this card OVERRULES (sets a base stat / disregards a
 * position rule / waives the drain) rather than nudging by +x/−x. A spec Action
 * hits one contest/action; a Condition is leaguewide and replaceable. (There is
 * no separate "Call" type any more — these are just Actions that carry a spec.)
 */
function OverrideRibbon({ kind, card }: { kind: string; card: HandCardData }) {
  const isCondition = kind === 'condition';
  const spec = card.spec;
  // SET a base stat · DISREGARD a position rule · RELIEF from the drain.
  let verb = 'OVERRIDE';
  let scope = isCondition ? ' · league' : ' · 1 contest';
  if (spec?.type === 'stat_set') verb = 'SET';
  else if (spec?.type === 'position_suppress') verb = 'DISREGARD';
  else if (spec?.type === 'stamina_relief') { verb = 'RELIEF'; scope = ' · 1 action'; }
  else if (isCondition) verb = /ignore|position|tier|magnet/i.test(card.effect ?? '') ? 'DISREGARD' : 'SET';
  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-col items-center">
      <span
        title={isCondition ? 'A Conditions card — a leaguewide ruling for all players, replaceable' : 'A set/suppress/relief action — overrules ONE contest or action, then it’s spent'}
        className="w-full bg-[var(--ec-pos-event)] py-0.5 text-center font-display text-[8.3cqw] font-bold uppercase leading-none tracking-[0.18em] text-[var(--ec-on-event)] shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
      >
        {isCondition ? 'Conditions' : 'Override'}
      </span>
      <span className="mt-0.5 rounded-sm border border-ec-brass/70 bg-ec-ground/85 px-1 py-0.5 font-display text-[7cqw] font-bold uppercase tracking-[0.12em] text-ec-brass">
        {verb}{scope}
      </span>
    </div>
  );
}

/** AFL's five stats in display order, with compact labels. */
const STAT_STRIP: ReadonlyArray<[string, string]> = [
  ['skill', 'SKL'],
  ['power', 'PWR'],
  ['speed', 'SPD'],
  ['intelligence', 'INT'],
  ['stamina', 'STA'],
];

function Body({ card }: { card: HandCardData }) {
  // Players show ALL FIVE stats as a compact strip (not just the top one).
  // Only render the strip when this looks like an AFL player (has the
  // 5-stat shape) — cricket players keep the text summary.
  if (card.kind === 'player' && card.stats && 'skill' in card.stats) {
    const s = card.stats;
    return (
      <div className="grid grid-cols-5 gap-px border-t border-ec-ink/20 bg-ec-face/0 px-0.5 py-1">
        {STAT_STRIP.map(([key, label]) => (
          <div key={key} className="flex flex-col items-center leading-none">
            <span className="ec-emboss font-display text-[11cqw] font-bold tabular-nums text-ec-ink">{s[key] ?? 0}</span>
            <span className="font-display text-[6.3cqw] font-semibold uppercase tracking-tight text-ec-ink-soft">{label}</span>
          </div>
        ))}
      </div>
    );
  }
  const text = bodyTextFor(card);
  if (!text) return null;
  // Rules text gets the serif voice, for gravity.
  return (
    <div className="ec-emboss border-t border-ec-ink/20 px-1.5 py-1 text-center font-serif text-[9.4cqw] font-medium leading-tight text-ec-ink">
      {text}
    </div>
  );
}

function Footer({ rarity }: { rarity: Rarity }) {
  // Footer band: set number + rarity GEM (rarity reads by colour off the
  // foil frame/header + this gem — no rarity TYPE spelled out).
  return (
    <div className="flex items-center justify-between border-t border-ec-ink/20 bg-ec-face-2 px-1.5 py-0.5 font-display text-[8.3cqw] uppercase tracking-wider text-ec-ink-soft">
      <span className="ec-emboss" title="Set">ECG · S1</span>
      <RarityGem rarity={rarity} className="h-3.5 w-3.5" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body text
// ---------------------------------------------------------------------------

function fmtBonus(n: number | undefined): string {
  if (typeof n !== 'number' || n === 0) return '';
  return ` ${n > 0 ? '+' : ''}${n}`;
}

const STAT_LABEL: Record<string, string> = {
  skill: 'Skill', power: 'Power', speed: 'Speed', intelligence: 'Intelligence', stamina: 'Stamina',
};
export function capStat(s?: string): string {
  return s ? (STAT_LABEL[s] ?? s.charAt(0).toUpperCase() + s.slice(1)) : '';
}

/**
 * A stadium's REAL effect — its permanent stat passive (and indoor flag), NOT
 * the abstract star rating. Stadiums modify a real stat for every player on
 * both sides for the rest of the match; indoor venues also cancel wind +
 * weather. A `bonus` of 0 is a do-nothing venue whose value is the indoor (or
 * just denying the opponent a ground).
 */
export function stadiumEffect(card: HandCardData): string {
  const b = typeof card.bonus === 'number' ? card.bonus : 0;
  if (b !== 0 && card.modifiesStat) {
    return `${capStat(card.modifiesStat)} ${b > 0 ? '+' : ''}${b}${card.indoor ? ' · Indoor' : ''}`;
  }
  return card.indoor ? 'Indoor — no wind or rain' : 'No stat effect';
}

function bodyTextFor(card: HandCardData): string | null {
  switch (card.kind) {
    case 'player': {
      // Show the highest stat as the player's signature. Works for
      // both AFL ({skill,power,...}) and cricket ({bat,bowl,...}).
      const stats = card.stats ?? {};
      const entries = Object.entries(stats) as Array<[string, number | undefined]>;
      const top = entries
        .filter(([, v]) => typeof v === 'number')
        .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))[0];
      return top ? `${top[0].toUpperCase()} ${top[1]}` : null;
    }
    case 'action': {
      // Cricket: shot vs delivery have their own bonuses.
      if (card.subkind === 'shot') {
        return `BAT${fmtBonus(card.batBonus)}${
          typeof card.risk === 'number' ? ` · risk ${card.risk}` : ''
        }`;
      }
      if (card.subkind === 'delivery') {
        return `BOWL${fmtBonus(card.bowlBonus)}${
          typeof card.wicketWeight === 'number' ? ` · wkt ×${card.wicketWeight}` : ''
        }`;
      }
      // AFL action — the card's own plain-language effect is the truth.
      return card.effect ?? 'Modifier';
    }
    case 'weather': {
      // AFL weather modifies a STAT (its `effect` reads "Skill −2"); cricket
      // weather still uses batterModifier until it's migrated.
      if (card.effect) return card.effect;
      if (typeof card.batterModifier === 'number') {
        return `Bat${fmtBonus(card.batterModifier)}`;
      }
      return null;
    }
    case 'stadium':
      // The REAL effect (stat passive + indoor), never the abstract star rating.
      return stadiumEffect(card);
    case 'time': {
      // Failing light docks every shot's skill by the hour index.
      const skill = -(typeof card.index === 'number' ? card.index : 0);
      return skill !== 0 ? `Skill ${skill} on every shot` : 'Full light — no penalty';
    }
    case 'condition':
      // A leaguewide OVERRIDE/SUPPRESS — the card's own plain-language effect is
      // the truth (e.g. "Set Power = 2 · all players", "Positions do not apply").
      // (Set/suppress/relief ACTIONS fall through the 'action' case above, which
      // already returns their `effect`.)
      return card.effect ?? 'Leaguewide override';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Glyphs — drawn to read on the recessed DARK art well (bone & brass lines).
// ---------------------------------------------------------------------------

/** A faint heraldic roundel + perched crow, watermarked behind every figure. */
function CrestWatermark() {
  return (
    <svg
      viewBox="-50 -50 100 100"
      className="pointer-events-none absolute left-1/2 top-1/2 h-[90%] w-[90%] -translate-x-1/2 -translate-y-1/2 text-ec-bone opacity-[0.07]"
      aria-hidden
    >
      <circle cx={0} cy={0} r={46} fill="none" stroke="currentColor" strokeWidth={2} />
      <circle cx={0} cy={0} r={39} fill="none" stroke="currentColor" strokeWidth={0.7} />
      {/* perched crow */}
      <path
        d="M-27 8 q19 -32 46 -29 q-11 9 -8 20 q14 -3 22 8 q-14 4 -25 6 q-19 8 -35 -3 z"
        fill="currentColor"
      />
      <path d="M19 -1 l10 -5 l-7 7 z" fill="currentColor" />
    </svg>
  );
}

function KindGlyph({ kind, card }: { kind: string; card: HandCardData }) {
  if (kind === 'player') return <PlayerGlyph position={card.position} role={card.role} />;
  if (kind === 'action') return <ActionGlyph />;
  if (kind === 'weather') return <WeatherGlyph />;
  if (kind === 'stadium') return <StadiumGlyph />;
  if (kind === 'condition') return <RulebookGlyph />;
  return <GenericGlyph />;
}

function PlayerGlyph({ position, role }: { position?: string; role?: string }) {
  // A simple silhouette; the jersey carries the position/role guernsey colour.
  const jersey =
    position === 'forward'
      ? 'var(--ec-pos-forward)'
      : position === 'midfielder'
        ? 'var(--ec-pos-mid)'
        : position === 'ruck'
          ? 'var(--ec-pos-ruck)'
          : position === 'back'
            ? 'var(--ec-pos-defence)'
            : role === 'batsman'
              ? 'var(--ec-pos-mid)'
              : role === 'bowler'
                ? 'var(--ec-pos-forward)'
                : role === 'all_rounder'
                  ? 'var(--ec-pos-ruck)'
                  : 'var(--ec-pos-event)';
  return (
    <svg viewBox="-12 -16 24 32" className="h-3/4 w-3/4">
      {/* Head */}
      <circle cx={0} cy={-9} r={3.4} fill="#f4f4f2" stroke="#13161b" strokeWidth={0.4} />
      {/* Jersey */}
      <path
        d="M-7 -4 L-3 -6 L3 -6 L7 -4 L7 6 L-7 6 Z"
        fill={jersey}
        stroke="#0b0d10"
        strokeWidth={0.5}
      />
      {/* Shorts */}
      <rect x={-6} y={6} width={12} height={5} fill="#cfd4d7" stroke="#0b0d10" strokeWidth={0.4} />
    </svg>
  );
}

function ActionGlyph() {
  return (
    <svg viewBox="-12 -12 24 24" className="h-3/4 w-3/4">
      <path
        d="M-8 0 L4 -7 L4 -3 L8 -3 L8 3 L4 3 L4 7 Z"
        fill="var(--ec-brass)"
        stroke="#13161b"
        strokeWidth={0.5}
      />
    </svg>
  );
}

function WeatherGlyph() {
  return (
    <svg viewBox="-12 -12 24 24" className="h-3/4 w-3/4">
      <circle cx={4} cy={-6} r={3} fill="var(--ec-brass-2)" />
      <ellipse cx={-2} cy={2} rx={8} ry={4} fill="#b7bfc7" />
      <ellipse cx={3} cy={3} rx={6} ry={3.2} fill="#dde1e3" />
    </svg>
  );
}

function StadiumGlyph() {
  return (
    <svg viewBox="-12 -10 24 20" className="h-3/4 w-3/4">
      <ellipse cx={0} cy={2} rx={10} ry={5} fill="var(--ec-pos-defence)" stroke="#0c1a10" strokeWidth={0.5} />
      <rect x={-9} y={-6} width={18} height={4} fill="#252c36" stroke="#0b0d10" strokeWidth={0.4} />
      <line x1={-7} x2={-7} y1={-2} y2={-6} stroke="var(--ec-brass)" strokeWidth={0.5} />
      <line x1={0} x2={0} y1={-2} y2={-7} stroke="var(--ec-brass)" strokeWidth={0.5} />
      <line x1={7} x2={7} y1={-2} y2={-6} stroke="var(--ec-brass)" strokeWidth={0.5} />
    </svg>
  );
}

function GenericGlyph() {
  return (
    <svg viewBox="-10 -10 20 20" className="h-3/4 w-3/4">
      <rect x={-6} y={-8} width={12} height={16} rx={1.5} fill="#cfd4d7" stroke="#0b0d10" strokeWidth={0.4} />
    </svg>
  );
}

/** A Condition — the rulebook/clipboard: the leaguewide ruling everyone plays under. */
function RulebookGlyph() {
  return (
    <svg viewBox="-12 -12 24 24" className="h-3/4 w-3/4">
      <rect x={-7} y={-9} width={14} height={18} rx={1.4} fill="#cfd4d7" stroke="#0b0d10" strokeWidth={0.5} />
      <rect x={-3} y={-11} width={6} height={3} rx={1} fill="var(--ec-brass)" stroke="#13161b" strokeWidth={0.4} />
      <line x1={-4} x2={4} y1={-3} y2={-3} stroke="#13161b" strokeWidth={0.7} />
      <line x1={-4} x2={4} y1={1} y2={1} stroke="#13161b" strokeWidth={0.7} />
      <line x1={-4} x2={1} y1={5} y2={5} stroke="#13161b" strokeWidth={0.7} />
    </svg>
  );
}
